import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { DeleteIconButton } from '../ui/DeleteIconButton';

export type RequirementSummary = {
  id: string;
  title: string;
  validationCriteria: string;
};

type RelatedRequirementsSectionProps = {
  sourceKey: string;
  canWrite: boolean;
  emptyCatalogMessage: string;
  loadCatalog: () => Promise<RequirementSummary[]>;
  loadLinked: () => Promise<RequirementSummary[]>;
  onLink: (requirementId: string) => Promise<void>;
  onUnlink: (requirementId: string) => Promise<void>;
};

function truncateText(value: string, maxLength: number): string {
  const text = value.trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

export function RelatedRequirementsSection({
  sourceKey,
  canWrite,
  emptyCatalogMessage,
  loadCatalog,
  loadLinked,
  onLink,
  onUnlink,
}: RelatedRequirementsSectionProps) {
  const [catalog, setCatalog] = useState<RequirementSummary[]>([]);
  const [linked, setLinked] = useState<RequirementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([loadCatalog(), loadLinked()])
      .then(([nextCatalog, nextLinked]) => {
        if (cancelled) return;
        setCatalog(nextCatalog);
        setLinked(nextLinked);
        setSelectedId('');
      })
      .catch((error) => {
        if (!cancelled) toast.error(getRequestErrorMessage(error, 'Falha ao carregar requisitos.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Recarrega quando a entrega muda. As funções acompanham essa chave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  const linkedIds = new Set(linked.map((item) => item.id));
  const available = catalog.filter((item) => !linkedIds.has(item.id));

  async function handleLink(): Promise<void> {
    if (!selectedId) return;
    setBusy(true);
    try {
      await onLink(selectedId);
      const [nextCatalog, nextLinked] = await Promise.all([loadCatalog(), loadLinked()]);
      setCatalog(nextCatalog);
      setLinked(nextLinked);
      setSelectedId('');
      toast.success('Requisito associado à entrega.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao associar requisito.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnlink(requirementId: string): Promise<void> {
    setBusy(true);
    try {
      await onUnlink(requirementId);
      setLinked((current) => current.filter((item) => item.id !== requirementId));
      toast.success('Requisito desassociado.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao desassociar requisito.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-xl border border-secondary-200 p-3 md:col-span-3">
      <div>
        <h3 className="text-sm font-semibold text-secondary-900">Requisitos relacionados</h3>
        <p className="text-xs text-secondary-500">Requisitos que esta entrega atende.</p>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-500">Carregando requisitos...</p>
      ) : catalog.length === 0 ? (
        <p className="text-sm text-secondary-500">{emptyCatalogMessage}</p>
      ) : (
        <>
          {linked.length === 0 ? (
            <p className="text-sm text-secondary-500">Nenhum requisito associado a esta entrega.</p>
          ) : (
            <ul className="divide-y divide-secondary-100">
              {linked.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="font-medium text-secondary-900" title={item.title}>
                      {truncateText(item.title, 200)}
                    </p>
                    <p className="text-xs text-secondary-500" title={item.validationCriteria}>
                      {truncateText(item.validationCriteria, 200)}
                    </p>
                  </div>
                  {canWrite ? (
                    <DeleteIconButton
                      label="Desassociar requisito"
                      disabled={busy}
                      onClick={() => void handleUnlink(item.id)}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {canWrite ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="erp-label min-w-[16rem] flex-1">
                Associar requisito
                <select
                  className="erp-input mt-1"
                  value={selectedId}
                  disabled={busy || available.length === 0}
                  onChange={(event) => setSelectedId(event.target.value)}
                >
                  <option value="">{available.length === 0 ? 'Todos já estão associados' : 'Selecione'}</option>
                  {available.map((item) => (
                    <option key={item.id} value={item.id}>
                      {truncateText(item.title, 200)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="erp-btn-secondary" disabled={busy || !selectedId} onClick={() => void handleLink()}>
                Associar
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
